/**
 * Octopus IoT Platform — Trip Detail Page (Phase 05-03)
 * Visão de detalhe da viagem. Read-only (D-08): zero botões de despacho.
 * Consome GET /api/operations/trips/{id} enriquecido (Plan 05-01) em UMA chamada (D-09).
 * Rota: /operations/trips/:tripId — gated por trip:read.
 * INV-UI-TRAIL-001: nenhum recálculo de telemetria; só exibição dos campos do backend.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import {
  Route,
  ChevronLeft,
  BarChart2,
  Sigma,
  Layers,
  Fuel,
  ClipboardList,
  FileSearch,
  AlertTriangle,
  Clock,
  RefreshCw,
  Loader2,
  PlayCircle,
  Radio,
  Lock,
  Pencil,
  Trash2,
  Plus,
  Save,
  Paperclip,
  Upload,
  Download,
  FileText,
  Image as ImageIcon,
  MessageSquare,
  CloudSun,
  Wind,
  Droplets,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import DateTimePicker24h from '@/components/ui/datetime-picker';
import { tripsApi } from '@/services/tripsApi';
import { useTimezone } from '@/contexts/TimezoneContext';

// --- Helpers (copiados verbatim de OperationsActivityPage.jsx + TripsPage.js) ---

const MOTOR_LABEL = {
  engine1: 'Draga',
  port: 'Bombordo',
  starboard: 'Boreste',
  center: 'Central',
  main: 'Principal',
};

// Compatibilidade backward: docs antigos com subdir=ida/volta (Phase 11 fará a migração completa).
const LEGACY_CARGO = { ida: 'carregado', volta: 'vazio' };
const cargoOf = (o) => o?.cargo_state || LEGACY_CARGO[o?.subdir] || null;

const STAGE_LABEL = {
  carregando: 'Carregando',
  descarregando: 'Descarregando',
  parado: 'Parado',
  viagem_carregado: 'Em deslocamento (carregado)',
  viagem_vazio: 'Em deslocamento (vazio)',
  viagem: 'Em deslocamento', // fallback sem cargo_state
};

// Rótulo humano do modo da regra R1 (parada fora de operação) — o enum cru do
// servidor (hibrido/duracao/motor) nunca aparece na tela.
const IDLE_MODE_LABEL = {
  hibrido: 'híbrido (duração + motor)',
  duracao: 'só duração',
  motor: 'só motor',
};

// Mapeamento de cor para badges de etapa (UI-SPEC Cargo State / Stage Label Matrix).
const STAGE_BADGE = {
  carregando: 'bg-amber-500 hover:bg-amber-500 text-black border-transparent',
  descarregando: 'bg-sky-500 hover:bg-sky-500 text-white border-transparent',
  parado: 'bg-muted text-muted-foreground border-transparent',
  viagem_carregado: 'bg-emerald-600 hover:bg-emerald-600 text-white border-transparent',
  viagem_vazio: 'bg-emerald-600 hover:bg-emerald-600 text-white border-transparent',
  viagem: 'bg-emerald-600 hover:bg-emerald-600 text-white border-transparent',
};

// Selo de status (verbatim de TripsPage.js). INV-UI-TRAIL-001: só display.
const STATUS_BADGE = {
  planejada: { label: 'Planejada', cls: 'bg-sky-600 hover:bg-sky-600 text-white border-transparent' },
  em_andamento: { label: 'Em andamento', cls: 'bg-amber-500 hover:bg-amber-500 text-black border-transparent' },
  fechamento_proposto: { label: 'Fechamento proposto', cls: 'bg-violet-600 hover:bg-violet-600 text-white border-transparent' },
  fechada: { label: 'Fechada', cls: 'bg-emerald-600 hover:bg-emerald-600 text-white border-transparent' },
  cancelada: { label: 'Cancelada', cls: 'bg-muted text-muted-foreground border-transparent line-through' },
};

const fmtDuration = (s) => {
  if (s == null || !Number.isFinite(Number(s))) return '—';
  const total = Math.round(Number(s));
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  if (hh > 0) return `${hh}h ${mm}min`;
  if (mm > 0) return `${mm}min`;
  return `${total}s`;
};

// Formata litros (milhar pt-BR). null/undefined → '—'. NÃO calcula nada — INV-UI-TRAIL-001.
const fmtLitros = (v) =>
  v == null ? '—' : `${Math.round(v).toLocaleString('pt-BR')} L`;

const fmtLph = (v) =>
  v == null || !Number.isFinite(Number(v)) ? '—' : `${Number(v).toFixed(1)} L/h`;

// Formata horas-motor (horímetro). null/undefined → '—'. NÃO calcula — INV-UI-TRAIL-001 (Phase 7).
const fmtHoras = (v) =>
  v == null || !Number.isFinite(Number(v)) ? '—' : `${Number(v).toFixed(1)} h`;

// Formata km medidos. null/undefined → '—'. NÃO calcula — INV-UI-TRAIL-001.
const fmtKm = (v) =>
  v == null || !Number.isFinite(Number(v)) ? '—' : `${Number(v).toFixed(1)} km`;

// Formata contagem inteira (ciclos de carga/descarga). null/undefined → '—'.
const fmtCount = (v) =>
  v == null || !Number.isFinite(Number(v)) ? '—' : `${Math.round(Number(v))}`;

// Soma os valores de um mapa {motor: horas} (horimetro_by_motor do segmento). Retorna null se vazio.
const sumHoras = (byMotor) => {
  if (!byMotor || typeof byMotor !== 'object') return null;
  const vals = Object.values(byMotor).filter((x) => Number.isFinite(Number(x)));
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + Number(b), 0);
};

// Agrega actual.horimetro_by_stage ({etapa: {by_motor_h, total_h}}) em {motorCurto: horas}.
// As etapas são carregado/vazio/chegada_manobra_h; cada uma traz by_motor_h por id curto.
const horimetroByMotorFromStages = (byStage) => {
  const out = {};
  if (!byStage || typeof byStage !== 'object') return out;
  for (const stage of Object.values(byStage)) {
    const bm = stage?.by_motor_h;
    if (!bm || typeof bm !== 'object') continue;
    for (const [motor, h] of Object.entries(bm)) {
      if (!Number.isFinite(Number(h))) continue;
      out[motor] = (out[motor] || 0) + Number(h);
    }
  }
  return out;
};

// Deriva o id curto do motor a partir da chave de fuel_by_motor (path canônico) OU id curto.
// 'propulsion.port.fuel.rate' → 'port'; 'port' → 'port'. Alinha as duas tabelas (Phase 7).
const shortMotor = (key) => {
  if (!key) return key;
  if (key.includes('.')) {
    const parts = key.split('.');
    return parts.length >= 2 ? parts[1] : key;
  }
  return key;
};

// Retorna a chave de badge/label para um segmento.
const stageKey = (seg) => {
  const cs = cargoOf(seg);
  return seg.activity === 'viagem' && cs ? `viagem_${cs}` : seg.activity;
};

// Destino formatado a partir do primeiro stop da viagem (sem lookup de rotas — D-09).
const fmtDestinoLocal = (trip) => {
  const stop = trip?.planned?.stops?.[0] || {};
  return stop.porto || stop.route_id || '—';
};

// Mesmo mount point do Socket.IO usado por useWebSocketTelemetry.js (espelhado).
const SOCKET_PATH = '/api/socket.io';

// --------------------------------------------------------------------------

function TripDetailPage() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();

  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  // null | 'not_found' (404 — "não encontrada" OU "sem acesso" — stealth, Pitfall 3) | 'server_error'
  const [error, setError] = useState(null);
  // Ações do ciclo de despacho em curso (desabilitam os botões enquanto a chamada está no ar).
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);

  // --- Seção "Dados da viagem" editável (Task 3) ---
  const [isEditing, setIsEditing] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [equipment, setEquipment] = useState(null);          // EquipmentResponse (carrega cargo_capacity_t)
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [volume, setVolume] = useState('');                  // string (input controlado)
  const [volumeSource, setVolumeSource] = useState(null);    // 'nominal_default' | 'manual' | null
  const [nominalDefault, setNominalDefault] = useState(null); // capacidade nominal do modo (ou null)
  const [crew, setCrew] = useState([]);                      // [{nome, funcao}]
  const [observacoes, setObservacoes] = useState('');

  // --- Phase 9: Anexos (REQ-29/REQ-11) ---
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);        // File | null
  const [uploadNote, setUploadNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // --- Phase 9: Briefing climático (REQ-30) ---
  const [weather, setWeather] = useState(null);              // { archived, briefing } | null
  const [weatherLoading, setWeatherLoading] = useState(false);

  const fetchTrip = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tripsApi.getTrip(tripId);
      setTrip(res.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setError('not_found');
      } else {
        setError('server_error');
      }
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    fetchTrip();
  }, [fetchTrip]);

  const reload = () => fetchTrip();

  // --- Acompanhar "ao vivo" (D-03/INV-UI-TRAIL-001) ---
  // O socket é APENAS um gatilho de re-fetch: quando o worker emite lab_activity_update
  // para o equipamento desta viagem, re-buscamos GET /{trip_id} e renderizamos os campos
  // já computados pelo backend. NUNCA lemos fuel_l/duration_s do payload para renderizar.
  const equipmentIdRef = useRef(null);
  const refetchInFlightRef = useRef(false);
  equipmentIdRef.current = trip?.equipment_id || null;

  useEffect(() => {
    const token = window.sessionStorage.getItem('octopus_token');
    const socket = io(process.env.REACT_APP_BACKEND_URL, {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      auth: token ? { token } : undefined,
    });

    socket.on('connect', () => {
      socket.emit('join_room', { room_id: 'dashboard' });
    });

    socket.on('lab_activity_update', async (payload) => {
      // Gatilho only: filtra pelo equipamento desta viagem e re-busca o doc autoritativo.
      if (!payload || payload.equipment_id !== equipmentIdRef.current) return;
      if (refetchInFlightRef.current) return; // guard anti-pilha (Pitfall 4)
      refetchInFlightRef.current = true;
      try {
        await fetchTrip();
      } finally {
        refetchInFlightRef.current = false;
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [fetchTrip]);

  // --- Ações do ciclo de despacho (apenas chamam endpoints; status é do worker) ---

  // Abrir despacho (D-02): grava o marcador append-only dispatch.opened_*; não muda status.
  const handleOpenDispatch = useCallback(async () => {
    setOpening(true);
    try {
      await tripsApi.openDispatch(tripId);
      toast.success('Despacho aberto.');
      await fetchTrip();
    } catch (err) {
      toast.error('Não foi possível abrir o despacho. Tente novamente.');
    } finally {
      setOpening(false);
    }
  }, [tripId, fetchTrip]);

  // Fechar despacho (D-04): reusa confirm-closure (fechamento_proposto→fechada). Selagem imutável.
  const handleConfirmClosure = useCallback(async () => {
    setClosing(true);
    try {
      await tripsApi.confirmClosure(tripId);
      toast.success('Despacho fechado. Viagem selada.');
      await fetchTrip();
    } catch (err) {
      toast.error('Não foi possível fechar o despacho. Tente novamente.');
    } finally {
      setClosing(false);
    }
  }, [tripId, fetchTrip]);

  // --- Phase 9: Anexos (REQ-29/REQ-11) ---

  const fetchAttachments = useCallback(async () => {
    setAttachmentsLoading(true);
    try {
      const res = await tripsApi.listAttachments(tripId);
      setAttachments(res.data?.attachments || []);
    } catch (err) {
      // não bloqueia a página; só esvazia a lista
      setAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [tripId]);

  const fetchWeather = useCallback(async () => {
    setWeatherLoading(true);
    try {
      const res = await tripsApi.getWeatherBriefing(tripId);
      setWeather(res.data || null);
    } catch (err) {
      setWeather(null);
    } finally {
      setWeatherLoading(false);
    }
  }, [tripId]);

  // Carrega anexos + briefing quando a viagem existe; re-carrega ao mudar o status
  // (ex.: fechamento → troca o briefing ao vivo pelo snapshot arquivado).
  // Sem recálculo no cliente — INV-UI-TRAIL-001.
  useEffect(() => {
    if (trip?.id) {
      fetchAttachments();
      fetchWeather();
    }
  }, [trip?.id, trip?.status, fetchAttachments, fetchWeather]);

  const editable = ['planejada', 'em_andamento', 'fechamento_proposto'].includes(trip?.status);

  const handleUpload = useCallback(async () => {
    const note = uploadNote.trim();
    if (!uploadFile && !note) {
      toast.error('Selecione um arquivo ou escreva uma observação.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      if (uploadFile) fd.append('file', uploadFile);
      if (note) fd.append('note', note);
      await tripsApi.uploadAttachment(tripId, fd);
      toast.success('Anexo registrado.');
      setUploadFile(null);
      setUploadNote('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchAttachments();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error('Viagem fechada/cancelada é imutável. Correções via errata.');
      } else if (err?.response?.status === 413) {
        toast.error('Arquivo muito grande (limite 25 MB).');
      } else if (err?.response?.status === 415) {
        toast.error('Tipo de arquivo não permitido.');
      } else {
        toast.error('Não foi possível registrar o anexo. Tente novamente.');
      }
    } finally {
      setUploading(false);
    }
  }, [tripId, uploadFile, uploadNote, fetchAttachments]);

  const handleDownload = useCallback(async (att) => {
    try {
      const res = await tripsApi.downloadAttachment(tripId, att.id);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'anexo';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Não foi possível baixar o anexo.');
    }
  }, [tripId]);

  const handleDeleteAttachment = useCallback(async (att) => {
    try {
      await tripsApi.deleteAttachment(tripId, att.id);
      toast.success('Anexo removido.');
      await fetchAttachments();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error('Viagem fechada/cancelada é imutável.');
      } else {
        toast.error('Não foi possível remover o anexo.');
      }
    }
  }, [tripId, fetchAttachments]);

  // --- Lançamento manual (equipamento sem telemetria — D-15) ---
  // Só aparece quando trip.no_telemetry. O cliente envia started_at/ended_at em ISO com o
  // offset fixo do projeto (mesmo padrão de TripsPage.createTrip); duration_s é derivado no
  // servidor. NUNCA muta status — o backend retorna fechamento_proposto. INV-UI-TRAIL-001.
  const [savingManual, setSavingManual] = useState(false);
  const [manHorimetro, setManHorimetro] = useState('');
  const [manFuel, setManFuel] = useState('');
  const [manStartedAt, setManStartedAt] = useState('');
  const [manEndedAt, setManEndedAt] = useState('');

  const handleManualActual = useCallback(async () => {
    const fuel = Number(manFuel);
    if (!Number.isFinite(fuel) || fuel <= 0) {
      toast.error('Informe o combustível total em litros (maior que zero).');
      return;
    }
    if (!manStartedAt || !manEndedAt) {
      toast.error('Informe o início e o fim da viagem.');
      return;
    }
    const payload = {
      fuel_total_l: fuel,
      // Mesmo padrão de TripsPage.createTrip: anexa segundos + offset fixo do projeto.
      started_at: `${manStartedAt}:00-03:00`,
      ended_at: `${manEndedAt}:00-03:00`,
    };
    const horStr = manHorimetro.trim();
    if (horStr !== '') {
      const hor = Number(horStr);
      if (!Number.isFinite(hor) || hor <= 0) {
        toast.error('Informe um horímetro válido em horas (maior que zero).');
        return;
      }
      payload.horimetro = hor;
    }
    setSavingManual(true);
    try {
      await tripsApi.manualActual(tripId, payload);
      toast.success('Realizado lançado. Viagem em fechamento proposto.');
      await fetchTrip();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        toast.error(
          typeof detail === 'string'
            ? detail
            : 'Lançamento manual indisponível: este equipamento possui telemetria.',
        );
      } else if (status === 422) {
        toast.error(
          typeof detail === 'string'
            ? detail
            : 'Dados inválidos. Verifique combustível, horímetro e as datas.',
        );
      } else {
        toast.error('Não foi possível lançar o realizado. Verifique a conexão e tente novamente.');
      }
    } finally {
      setSavingManual(false);
    }
  }, [tripId, manFuel, manHorimetro, manStartedAt, manEndedAt, fetchTrip]);

  // --- Edição da seção "Dados da viagem" ---

  // Entrar em modo edição: SEMPRE busca o equipment (única fonte da capacidade nominal — D-11),
  // pois o doc da viagem NUNCA traz cargo_capacity_t (GET /{trip_id} projeta só name/tag).
  const enterEdit = useCallback(async () => {
    const od = trip?.operational_data || {};
    // Pré-popular crew/observações com o que já existe.
    setCrew((od.crew || []).map((c) => ({ nome: c?.nome || '', funcao: c?.funcao || '' })));
    setObservacoes(od.observacoes || '');
    setIsEditing(true);
    setEquipmentLoading(true);

    let cap = null;
    try {
      const res = await tripsApi.getEquipment(trip.equipment_id);
      setEquipment(res.data);
      cap = res.data?.cargo_capacity_t?.[trip?.planned?.modo_carga];
    } catch (err) {
      setEquipment(null);
    } finally {
      setEquipmentLoading(false);
    }
    setNominalDefault(cap != null ? cap : null);

    // Pré-preenchimento do volume (D-11/D-12): valor já salvo > capacidade nominal > vazio.
    if (od.volume_t != null) {
      setVolume(String(od.volume_t));
      setVolumeSource(od.volume_source || 'manual');
    } else if (cap != null) {
      setVolume(String(cap));
      setVolumeSource('nominal_default');
    } else {
      setVolume('');
      setVolumeSource(null);
    }
  }, [trip]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const updateCrewRow = (idx, field, value) => {
    setCrew((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addCrewRow = () => setCrew((rows) => [...rows, { nome: '', funcao: '' }]);
  const removeCrewRow = (idx) => setCrew((rows) => rows.filter((_, i) => i !== idx));

  const handleSaveData = useCallback(async () => {
    // Validação cliente (UX; backend é server-authoritative — D-09).
    const volStr = volume.trim();
    let volume_t = null;
    let volume_source = null;
    if (volStr !== '') {
      const v = Number(volStr);
      if (!Number.isFinite(v) || v <= 0) {
        toast.error('Informe um volume válido em toneladas (maior que zero).');
        return;
      }
      volume_t = v;
      volume_source = volumeSource || 'manual';
    }
    // Crew: nome obrigatório se a linha tem função; descarta linhas totalmente vazias (Pitfall 5).
    const trimmed = crew.map((c) => ({ nome: c.nome.trim(), funcao: c.funcao.trim() }));
    if (trimmed.some((c) => c.funcao && !c.nome)) {
      toast.error('Informe o nome do tripulante.');
      return;
    }
    const crewPayload = trimmed.filter((c) => c.nome);

    setSavingData(true);
    try {
      await tripsApi.updateOperationalData(tripId, {
        volume_t,
        volume_source,
        crew: crewPayload,
        observacoes: observacoes.trim() || null,
      });
      toast.success('Dados da viagem salvos.');
      setIsEditing(false);
      await fetchTrip();
    } catch (err) {
      if (err?.response?.status === 409) {
        // Viagem fechou no meio da edição (Pitfall 3): correções por errata; força saída + re-fetch.
        toast.error('Esta viagem está fechada e não pode ser editada. Correções devem ser feitas por errata.');
        setIsEditing(false);
        await fetchTrip();
      } else {
        toast.error('Não foi possível salvar os dados. Verifique a conexão e tente novamente.');
      }
    } finally {
      setSavingData(false);
    }
  }, [tripId, volume, volumeSource, crew, observacoes, fetchTrip]);

  // --- Skeleton de carregamento (4 cards, um por seção) ---
  const renderSkeleton = () => (
    <>
      {[1, 2, 3, 4].map((i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-48" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </CardContent>
        </Card>
      ))}
    </>
  );

  // --- Estado "Viagem não encontrada" (404 — cobre "não encontrada" E "sem acesso", T-05-10/11) ---
  const renderNotFound = () => (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
      <FileSearch className="w-12 h-12 text-muted-foreground" />
      <h2 className="text-2xl font-semibold">Viagem não encontrada</h2>
      <p className="text-sm text-muted-foreground max-w-xs">
        O código VGM não foi encontrado. Verifique o endereço ou acesse a lista de viagens.
      </p>
      <Button variant="outline" className="gap-2" onClick={() => navigate('/operations/trips')}>
        <ChevronLeft className="w-4 h-4" />
        Voltar para Viagens
      </Button>
    </div>
  );

  // --- Estado de erro de rede / 5xx ---
  const renderServerError = () => (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
      <AlertTriangle className="w-12 h-12 text-destructive" />
      <h2 className="text-2xl font-semibold">Erro ao carregar a viagem.</h2>
      <p className="text-sm text-muted-foreground max-w-xs">
        Verifique a conexão e tente novamente.
      </p>
      <div className="flex gap-2 flex-wrap justify-center">
        <Button variant="outline" className="gap-2" onClick={reload}>
          <RefreshCw className="w-4 h-4" />
          Tentar novamente
        </Button>
        <Button variant="ghost" className="gap-2" onClick={() => navigate('/operations/trips')}>
          <ChevronLeft className="w-4 h-4" />
          Voltar para Viagens
        </Button>
      </div>
    </div>
  );

  // --- Section 1: Previsto × Realizado ---

  const renderPrevisto = () => {
    const planned = trip?.planned || {};
    const projected = trip?.projected || {};
    const stop = planned?.stops?.[0] || {};
    const saldoBaixo =
      projected.saldo_volta_proj != null &&
      projected.reserve_l != null &&
      projected.saldo_volta_proj < projected.reserve_l;

    return (
      <div>
        <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Previsto</h3>
        <dl className="space-y-1.5">
          <div>
            <dt className="text-xs text-muted-foreground">Destino</dt>
            <dd className="text-sm">{stop.porto || stop.route_id || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Modo de carga</dt>
            <dd className="text-sm capitalize">{planned.modo_carga || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Produto</dt>
            <dd className="text-sm text-muted-foreground">{stop.produto || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Data prevista de início</dt>
            <dd className="text-sm">
              {planned.start_at
                ? formatDateTime(planned.start_at, { omitSeconds: true })
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Retorno previsto</dt>
            <dd className="text-sm">
              {projected.retorno_previsto
                ? formatDateTime(projected.retorno_previsto, { omitSeconds: true })
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Média recente (planejamento)</dt>
            <dd className="text-sm">{fmtLitros(projected.consumo_previsto_l)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Saldo projetado no retorno</dt>
            <dd className={`text-sm${saldoBaixo ? ' text-amber-600 font-semibold' : ''}`}>
              {fmtLitros(projected.saldo_volta_proj)}
            </dd>
          </div>
        </dl>
      </div>
    );
  };

  const renderRealizado = () => {
    if (!trip?.actual) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center gap-2 text-muted-foreground">
          <Clock className="w-6 h-6" />
          <p className="text-sm font-semibold">Aguardando início da viagem</p>
          <p className="text-xs">
            Os dados de realizado serão preenchidos automaticamente pelo sistema.
          </p>
        </div>
      );
    }
    const { actual } = trip;
    return (
      <div>
        <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Realizado</h3>
        <dl className="space-y-1.5">
          <div>
            <dt className="text-xs text-muted-foreground">Destino real</dt>
            <dd className="text-sm">{actual.destino_detectado || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Data de início real</dt>
            <dd className="text-sm">
              {actual.started_at ? formatDateTime(actual.started_at) : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Data de fechamento</dt>
            <dd className="text-sm">
              {actual.ended_at ? formatDateTime(actual.ended_at) : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Consumo real</dt>
            <dd className="text-sm">{fmtLitros(actual.fuel_total_l)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Duração total</dt>
            <dd className="text-sm">{fmtDuration(actual.duration_s)}</dd>
          </div>
        </dl>
      </div>
    );
  };

  // --- Índices Previsto × Realizado (_indices do GET — computados no servidor) ---
  // INV-UI-TRAIL-001: exibição pura. 100% = na previsão; >100% = acima do previsto.
  const fmtIndicePct = (v) =>
    v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
  const indiceColor = (pct) => {
    // Escala vs ALVO otimizado (dono 2026-07-06): ≤102% no/quase-no alvo = verde;
    // 102,1–105% acima do alvo = atenção (âmbar); >105% = vermelho.
    if (pct == null || !Number.isFinite(pct)) return 'text-muted-foreground';
    if (pct <= 102) return 'text-emerald-500';
    if (pct <= 105) return 'text-amber-500';
    return 'text-red-500';
  };
  const fmtDesvio = (v, fmt) => {
    if (v == null || !Number.isFinite(v)) return '—';
    return `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmt(Math.abs(v))}`;
  };

  const trendMeta = (tr) => {
    if (!tr || tr.direcao === 'indefinida' || tr.variacao_pct == null) return null;
    const map = {
      melhorando: { arrow: '↓', cls: 'text-emerald-500', txt: 'melhorando' },
      piorando: { arrow: '↑', cls: 'text-red-500', txt: 'piorando' },
      estavel: { arrow: '→', cls: 'text-muted-foreground', txt: 'estável' },
    };
    const m = map[tr.direcao] || map.estavel;
    const pct = Math.abs(tr.variacao_pct).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    return { cls: m.cls, label: `${m.arrow} ${m.txt} ${pct}%` };
  };

  const renderIndices = () => {
    const idx = trip?._indices;
    if (!idx) return null;
    const tend = trip?._tendencia;
    const rows = [];
    if (idx.combustivel) {
      const c = idx.combustivel;
      rows.push({
        key: 'combustivel', label: 'Combustível',
        alvo: fmtLitros(c.previsto_l), media: fmtLitros(c.media_recente_l),
        realizado: fmtLitros(c.realizado_l), desvio: fmtDesvio(c.desvio_l, fmtLitros),
        pct: c.indice_pct, reancorado: c.reancorado,
        destino_planejado: c.destino_planejado, destino_real: c.destino_real,
        sem_alvo: c.sem_alvo, alvo_original: fmtLitros(c.previsto_original_l),
        trend: trendMeta(tend?.combustivel),
      });
    }
    if (idx.tempo) {
      const t = idx.tempo;
      // R1 (parada fora de operação, dono 2026-07-07): quando o servidor computa a
      // duração PRODUTIVA (t.produtiva), realizado_s JÁ é a produtiva e o alvo é o
      // alvo produtivo — aqui só EXIBIMOS a decomposição (INV-UI-TRAIL-001).
      const comFora = t.produtiva && Number.isFinite(t.fora_operacao_s) && t.fora_operacao_s > 0;
      const idleXh = t.idle_rule?.min_min != null
        ? (t.idle_rule.min_min / 60).toLocaleString('pt-BR', { maximumFractionDigits: 1 })
        : null;
      rows.push({
        key: 'tempo', label: 'Tempo',
        alvo: fmtDuration(t.previsto_s), media: fmtDuration(t.media_recente_s),
        realizado: fmtDuration(t.realizado_s), desvio: fmtDesvio(t.desvio_s, fmtDuration),
        realizadoLabel: comFora ? 'Produtivo' : null,
        pct: t.indice_pct, reancorado: t.reancorado,
        destino_planejado: t.destino_planejado, destino_real: t.destino_real,
        sem_alvo: t.sem_alvo, alvo_original: fmtDuration(t.previsto_original_s),
        trend: trendMeta(tend?.tempo),
        // A equação fica visível; os parâmetros da regra (auditoria de design
        // 2026-07-07) vão humanizados para o tooltip — nada de enum cru na tela.
        produtivaNote: comFora
          ? `tempo produtivo ${fmtDuration(t.realizado_s)} = ${fmtDuration(t.duracao_total_s)} − ` +
            `${fmtDuration(t.fora_operacao_s)} fora de operação`
          : null,
        produtivaTitle: comFora && t.idle_rule
          ? `Regra: ${IDLE_MODE_LABEL[t.idle_rule.mode] || t.idle_rule.mode}` +
            `${idleXh ? ` · parada mínima ${idleXh}h` : ''} · motor ligado = RPM > 1`
          : undefined,
      });
    }
    if (!rows.length) return null;
    const temReancora = rows.some((r) => r.reancorado);
    // Ordem de exibição dos motores (dono 2026-07-06): bombordo · boreste / somada · draga.
    const motorRank = (m) => {
      if (m.role === 'propulsion' && m.position === 'port') return 0;
      if (m.role === 'propulsion' && m.position === 'starboard') return 1;
      if (m.key === '__propulsao__') return 2;
      if (m.role === 'draga') return 3;
      return 4;
    };
    const motores = Array.isArray(trip?._indices_motores)
      ? [...trip._indices_motores].sort((a, b) => motorRank(a) - motorRank(b))
      : [];
    return (
      <Card className="border-primary/30 shadow-sm" data-testid="trip-indices">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-3 flex-wrap">
            <BarChart2 className="w-4 h-4 text-primary" />
            Índice de desempenho
            <span className="text-xs font-normal text-muted-foreground">vs alvo otimizado</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {rows.map((r) => (
              <div key={r.key} className="rounded-lg border bg-muted/20 p-4">
                {/* Cabeçalho da métrica: rótulo + badge de desvio de destino */}
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="text-sm font-semibold">{r.label}</span>
                  {r.reancorado && (
                    <span className="inline-block rounded bg-amber-500/15 text-amber-600 px-1.5 py-0.5 text-[10px] font-medium">
                      desvio {r.destino_planejado}→{r.destino_real}
                    </span>
                  )}
                </div>

                {/* Protagonista: índice % em número grande + tendência */}
                {r.sem_alvo ? (
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-4xl font-bold tracking-tight text-muted-foreground leading-none">—</span>
                    <span className="text-xs text-muted-foreground">
                      sem alvo p/ {r.destino_real || 'este destino'}
                    </span>
                    {r.trend && (
                      <span className={`text-sm font-medium ${r.trend.cls}`}>{r.trend.label}</span>
                    )}
                  </div>
                ) : (
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className={`text-4xl font-bold tracking-tight leading-none ${indiceColor(r.pct)}`}>
                      {fmtIndicePct(r.pct)}
                    </span>
                    {r.trend && (
                      <span className={`text-sm font-medium ${r.trend.cls}`}>{r.trend.label}</span>
                    )}
                  </div>
                )}

                {/* Alvo · realizado · desvio */}
                <dl className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <dt className="text-muted-foreground">Alvo</dt>
                    <dd className="text-sm font-medium tabular-nums">{r.sem_alvo ? '—' : r.alvo}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{r.realizadoLabel || 'Realizado'}</dt>
                    <dd className="text-sm font-medium tabular-nums">{r.realizado}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Desvio</dt>
                    <dd className="text-sm font-medium tabular-nums">{r.sem_alvo ? '—' : r.desvio}</dd>
                  </div>
                </dl>

                {/* Decomposição do tempo produtivo (R1) — número explicável, vem pronto do servidor */}
                {r.produtivaNote && (
                  <p
                    className="text-[11px] text-muted-foreground/80 mt-3 pt-2 border-t cursor-help"
                    title={r.produtivaTitle}
                    data-testid="trip-indice-produtiva"
                  >
                    {r.produtivaNote}
                  </p>
                )}

                {/* Base de planejamento (secundária, não compete com o alvo) */}
                <p className={`text-[11px] text-muted-foreground/80 ${r.produtivaNote ? 'mt-1' : 'mt-3 pt-2 border-t'}`}>
                  média recente (planejamento) {r.media}
                  {r.reancorado && !r.sem_alvo && ` · alvo original (${r.destino_planejado}): ${r.alvo_original}`}
                </p>
              </div>
            ))}
          </div>

          {/* Índice por MOTOR (role) — mesma regra da viagem, por fonte de consumo */}
          {motores.length > 0 && (
            <div className="mt-4 pt-3 border-t">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Combustível por motor (vs alvo)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {motores.map((m) => (
                  <div key={m.key} className="flex items-center justify-between gap-2 rounded-md border bg-muted/10 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{m.label}</p>
                      <p className="text-[11px] text-muted-foreground tabular-nums">
                        alvo {m.sem_alvo ? '—' : fmtLitros(m.alvo_l)} · realizado {fmtLitros(m.realizado_l)}
                      </p>
                    </div>
                    <span className={`text-lg font-bold tabular-nums shrink-0 ${indiceColor(m.indice_pct)}`}>
                      {m.sem_alvo ? '—' : fmtIndicePct(m.indice_pct)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground mt-3">
            Índice = realizado ÷ <strong>alvo</strong> (média do melhor terço do destino). 100% = no alvo; acima = consumiu/durou mais. A <strong>média recente</strong> é a base de planejamento; a <strong>tendência</strong> mostra a evolução mês a mês.
            {temReancora && ' Sob desvio, alvo e média vêm do destino REAL.'}
            {rows.some((r) => r.produtivaNote) &&
              ' No tempo, o realizado é a duração PRODUTIVA (paradas fora de operação não contam).'}
          </p>
        </CardContent>
      </Card>
    );
  };

  // --- Section 1.5: Totais da viagem (bloco _totais do GET — totalizadores server-side) ---
  // INV-UI-TRAIL-001: exibição pura; None gracioso (U-10 — DRA-0005 rebocada → propulsão '—').

  const renderTotais = () => {
    const totais = trip?._totais;
    if (!totais || Object.keys(totais).length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center gap-2 text-muted-foreground">
          <Sigma className="w-6 h-6" />
          <p className="text-sm font-semibold">Aguardando telemetria do ciclo</p>
          <p className="text-xs">
            Os totalizadores são calculados pelo servidor quando a viagem tem um ciclo vinculado.
          </p>
        </div>
      );
    }
    const items = [
      { label: 'Combustível — draga', value: fmtLitros(totais.fuel_draga_l) },
      { label: 'Combustível — propulsão', value: fmtLitros(totais.fuel_propulsao_l) },
      { label: 'Combustível — total', value: fmtLitros(totais.fuel_total_l) },
      { label: 'Distância medida', value: fmtKm(totais.measured_distance_km) },
      { label: 'Duração ativa', value: fmtDuration(totais.duration_ativa_s) },
      { label: 'Tempo parado', value: fmtDuration(totais.parado_s) },
      { label: 'Ciclos de carga', value: fmtCount(totais.n_carga) },
      { label: 'Descargas', value: fmtCount(totais.n_descarga) },
    ];
    return (
      <div>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
          {items.map(({ label, value }) => (
            <div key={label}>
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd className="text-sm">{value}</dd>
            </div>
          ))}
        </dl>
        <p className="text-xs text-muted-foreground mt-3">
          Totais do ciclo inteiro (incluem o trabalho de bomba na carga/descarga); a tabela de
          Etapas detalha combustível apenas das pernas de deslocamento.
          {totais.base_anchored ? ' Viagem ancorada na base.' : ''}
        </p>
      </div>
    );
  };

  // --- Section 2: Etapas ---

  const renderEtapas = () => {
    const hasStages = trip?.actual && trip?._stages?.length > 0;
    const legs = trip?._legs || {};

    if (!hasStages) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-2 text-muted-foreground">
          <Layers className="w-6 h-6" />
          <p className="text-sm font-semibold">Aguardando início da viagem</p>
          <p className="text-xs">
            As etapas serão registradas automaticamente conforme a operação avançar.
          </p>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-sm font-semibold">Etapa</TableHead>
            <TableHead className="text-sm font-semibold">Início</TableHead>
            <TableHead className="text-sm font-semibold">Fim</TableHead>
            <TableHead className="text-sm font-semibold">Duração</TableHead>
            <TableHead className="text-sm font-semibold">Combustível</TableHead>
            <TableHead className="text-sm font-semibold">L/h</TableHead>
            <TableHead className="text-sm font-semibold">Horímetro</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {trip._stages.map((seg, idx) => {
            const key = stageKey(seg);
            const label = STAGE_LABEL[key] || key || '—';
            const badgeCls = STAGE_BADGE[key] || 'bg-muted text-muted-foreground border-transparent';
            const cs = cargoOf(seg);
            // L/h POR ETAPA vem pronto do servidor (seg.lph — CC-2026-07-04);
            // fallback legado: média da perna por estado de carga (docs antigos
            // sem o campo — era o valor repetido que confundia a leitura).
            const lph = seg.lph ?? (seg.activity === 'viagem' && cs ? legs[cs]?.lph : null);
            // Parado ÚNICO com detalhamento por motor (dono 2026-07-07, mockup
            // aprovado): sub-linhas prontas do servidor (seg._motores).
            const motores = Array.isArray(seg._motores) ? seg._motores : [];

            return (
              <React.Fragment key={idx}>
                <TableRow className="hover:bg-muted/40">
                  <TableCell>
                    <Badge className={`rounded-md text-xs font-semibold ${badgeCls}`}>
                      {label}
                    </Badge>
                    {/* R1: só a EXCEÇÃO é marcada (parada fora de operação) —
                        chip neutro na linha-mãe; motivo do servidor no title. */}
                    {seg.classificacao_parada === 'fora_operacao' && (
                      <p className="mt-1">
                        <span
                          className="inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground cursor-help"
                          title={seg.parada_motivo || undefined}
                        >
                          fora de operação
                          {Number.isFinite(seg.fora_operacao_s) && seg.fora_operacao_s !== seg.duration_s
                            ? ` (${fmtDuration(seg.fora_operacao_s)})`
                            : ''}
                        </span>
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {seg.started_at
                      ? formatDateTime(seg.started_at, { omitSeconds: true })
                      : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {seg.ended_at ? (
                      formatDateTime(seg.ended_at, { omitSeconds: true })
                    ) : (
                      <Badge className="rounded-md text-xs font-semibold bg-amber-500 hover:bg-amber-500 text-black border-transparent">
                        Em andamento
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{fmtDuration(seg.duration_s)}</TableCell>
                  <TableCell className="text-sm">{fmtLitros(seg.fuel_l)}</TableCell>
                  <TableCell className="text-sm">{fmtLph(lph)}</TableCell>
                  <TableCell className="text-sm">{fmtHoras(sumHoras(seg.horimetro_by_motor))}</TableCell>
                </TableRow>
                {/* Sub-linhas por motor: tempo ligado MEDIDO + combustível +
                    L/h médio do motor dentro da parada. A soma bate com a
                    linha-mãe; rajadas curtas aparecem no tempo do motor
                    independentemente de histerese (INV-UI-TRAIL-001: números
                    prontos do servidor). Horímetro fica VAZIO de propósito:
                    ligado_s (RPM) e o contador CANbus da mãe não reconciliam
                    — não induzir soma (auditoria de design, 3ª rodada). */}
                {motores.map((m) => (
                  <TableRow key={`${idx}-${m.motor ?? m.label}`} className="bg-muted/10 hover:bg-muted/20">
                    <TableCell className="py-1.5 pl-8 text-xs text-muted-foreground">
                      <span className="text-muted-foreground/60">└ </span>
                      <span className="font-medium text-foreground/80">{m.label}</span>
                      <span className="ml-1.5 text-[10px]">ligado</span>
                    </TableCell>
                    <TableCell className="py-1.5" colSpan={2}></TableCell>
                    <TableCell className="py-1.5 text-xs text-muted-foreground">{fmtDuration(m.ligado_s)}</TableCell>
                    <TableCell className="py-1.5 text-xs text-muted-foreground">{fmtLitros(m.fuel_l)}</TableCell>
                    <TableCell className="py-1.5 text-xs text-muted-foreground">{fmtLph(m.lph)}</TableCell>
                    <TableCell className="py-1.5"></TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  // --- Section 3: Consumo por motor ---

  const renderConsumoMotor = () => {
    if (!trip?.actual) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-2 text-muted-foreground">
          <Fuel className="w-6 h-6" />
          <p className="text-sm font-semibold">Aguardando telemetria</p>
          <p className="text-xs">
            O consumo por motor será calculado automaticamente conforme a telemetria for recebida.
          </p>
        </div>
      );
    }

    // Preferência: bloco `_consumo_motor` do servidor (CC-2026-07-04) — consumo,
    // horímetro (Σ dos segmentos da viagem DERIVADA) e MÉDIA L/h já resolvidos
    // (INV-UI-TRAIL-001). Fallback legado: actual (selagem) sem média.
    const cmServer = Array.isArray(trip._consumo_motor) ? trip._consumo_motor : [];
    const rows = cmServer.length > 0
      ? cmServer.map((m) => ({
          key: m.motor,
          label: MOTOR_LABEL[m.motor] || m.motor,
          fuel: m.fuel_l,
          media: m.media_lph,
          horas: m.horimetro_h,
        }))
      : Object.entries(trip.actual?.fuel_by_motor || {})
          .sort(([, a], [, b]) => b - a)
          .map(([motorKey, valor]) => {
            const short = shortMotor(motorKey);
            const horimetroByMotor = horimetroByMotorFromStages(trip.actual?.horimetro_by_stage);
            return {
              key: motorKey,
              label: MOTOR_LABEL[short] || MOTOR_LABEL[motorKey] || motorKey,
              fuel: valor,
              media: null,
              horas: horimetroByMotor[short],
            };
          });

    if (rows.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-2 text-muted-foreground">
          <Fuel className="w-6 h-6" />
          <p className="text-sm font-semibold">Aguardando telemetria</p>
          <p className="text-xs">
            O consumo por motor será calculado automaticamente conforme a telemetria for recebida.
          </p>
        </div>
      );
    }

    return (
      <>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-sm font-semibold">Motor</TableHead>
              <TableHead className="text-sm font-semibold">Consumo total (L)</TableHead>
              <TableHead className="text-sm font-semibold">Média L/h</TableHead>
              <TableHead className="text-sm font-semibold">Horímetro (h)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} className="hover:bg-muted/40">
                <TableCell className="text-sm">{r.label}</TableCell>
                <TableCell className="text-sm">{fmtLitros(r.fuel)}</TableCell>
                <TableCell className="text-sm">{fmtLph(r.media)}</TableCell>
                <TableCell className="text-sm">{fmtHoras(r.horas)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="text-xs text-muted-foreground mt-2">
          Horímetro = horas com o motor LIGADO na janela da viagem (contador do
          CANbus; inclui marcha lenta). Média L/h = consumo do motor ÷ horímetro.
        </p>
      </>
    );
  };

  // --- Section 4: Dados da viagem (leitura ↔ edição in-place) ---

  // Sufixo de origem do volume na leitura (D-11).
  const volumeSourceTag = (src) =>
    src === 'nominal_default' ? '(capacidade nominal)' : src === 'manual' ? '(manual)' : '';

  // Metadados imutáveis da viagem (sempre exibidos no topo da seção, em leitura e edição).
  const renderViagemMeta = () => {
    const planned = trip?.planned || {};
    const stop = planned?.stops?.[0] || {};
    return (
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
        <div>
          <dt className="text-xs text-muted-foreground">VGM</dt>
          <dd className="text-sm font-semibold font-mono text-xs">{trip.id}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Origem</dt>
          <dd className="text-sm font-semibold">{planned.origin || '—'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Destino</dt>
          <dd className="text-sm font-semibold">{fmtDestinoLocal(trip)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Modo de carga</dt>
          <dd className="text-sm font-semibold capitalize">{planned.modo_carga || '—'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Produto</dt>
          <dd className="text-sm font-semibold">{stop.produto || '—'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Criado por</dt>
          <dd className="text-sm text-muted-foreground">{trip.created_by?.name || trip.created_by?.username || '—'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Criado em</dt>
          <dd className="text-sm font-semibold">
            {trip.created_at ? formatDateTime(trip.created_at) : '—'}
          </dd>
        </div>
      </dl>
    );
  };

  // Leitura dos dados operacionais (volume/tripulação/observações).
  const renderOperationalView = () => {
    const od = trip?.operational_data || {};
    const crewList = od.crew || [];
    return (
      <div className="space-y-4">
        <div>
          <p className="text-xs text-muted-foreground">Volume transportado (t)</p>
          <p className="text-sm font-semibold">
            {od.volume_t != null ? (
              <>
                {od.volume_t} t{' '}
                <span className="text-xs font-normal text-muted-foreground">
                  {volumeSourceTag(od.volume_source)}
                </span>
              </>
            ) : (
              '—'
            )}
          </p>
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-1">Tripulação</p>
          {crewList.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum tripulante registrado.</p>
          ) : (
            <ul className="space-y-1">
              {crewList.map((c, i) => (
                <li key={i} className="text-sm">
                  <span className="font-semibold">{c.nome || '—'}</span>
                  {c.funcao ? <span className="text-muted-foreground"> — {c.funcao}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-1">Observações</p>
          <p className="text-sm whitespace-pre-wrap">
            {od.observacoes ? od.observacoes : <span className="text-muted-foreground">—</span>}
          </p>
        </div>
      </div>
    );
  };

  // Formulário de edição in-place.
  const renderOperationalEdit = () => {
    const volNum = volume.trim() === '' ? null : Number(volume);
    const volumeInvalid = volume.trim() !== '' && (!Number.isFinite(volNum) || volNum <= 0);
    const crewInvalid = crew.some((c) => c.funcao.trim() && !c.nome.trim());
    const capacityMissing = !equipmentLoading && nominalDefault == null && (trip?.operational_data?.volume_t == null);

    return (
      <div className="space-y-6">
        {/* Volume */}
        <div className="space-y-1.5 max-w-xs">
          <Label htmlFor="volume_t">Volume transportado (t)</Label>
          <Input
            id="volume_t"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.1"
            value={volume}
            disabled={savingData}
            onChange={(e) => {
              setVolume(e.target.value);
              setVolumeSource('manual'); // edição manual sobrepõe o default nominal
            }}
          />
          {capacityMissing && (
            <p className="text-xs text-muted-foreground">
              Capacidade nominal não configurada para este equipamento. Informe o volume manualmente.
            </p>
          )}
          {volumeInvalid && (
            <p className="text-xs text-destructive">
              Informe um volume válido em toneladas (maior que zero).
            </p>
          )}
        </div>

        {/* Cartão de tripulação */}
        <div className="space-y-2">
          <Label>Tripulação</Label>
          {crew.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Adicione o nome e a função de cada tripulante desta viagem.
            </p>
          ) : (
            <div className="space-y-2">
              {crew.map((c, idx) => {
                const rowInvalid = c.funcao.trim() && !c.nome.trim();
                return (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="flex-1 space-y-1">
                      <Input
                        aria-label="Nome"
                        placeholder="Nome"
                        value={c.nome}
                        disabled={savingData}
                        onChange={(e) => updateCrewRow(idx, 'nome', e.target.value)}
                      />
                      {rowInvalid && (
                        <p className="text-xs text-destructive">Informe o nome do tripulante.</p>
                      )}
                    </div>
                    <Input
                      aria-label="Função"
                      placeholder="Função"
                      className="flex-1"
                      value={c.funcao}
                      disabled={savingData}
                      onChange={(e) => updateCrewRow(idx, 'funcao', e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive shrink-0"
                      aria-label="Remover tripulante"
                      disabled={savingData}
                      onClick={() => removeCrewRow(idx)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={savingData}
            onClick={addCrewRow}
          >
            <Plus className="w-4 h-4" />
            Adicionar tripulante
          </Button>
        </div>

        {/* Observações */}
        <div className="space-y-1.5">
          <Label htmlFor="observacoes">Observações</Label>
          <Textarea
            id="observacoes"
            rows={3}
            maxLength={2000}
            placeholder="Anotações livres desta viagem (opcional)."
            value={observacoes}
            disabled={savingData}
            onChange={(e) => setObservacoes(e.target.value)}
          />
        </div>

        {/* Ações */}
        <div className="flex items-center gap-2">
          <Button
            className="gap-2"
            disabled={savingData || volumeInvalid || crewInvalid}
            onClick={handleSaveData}
          >
            {savingData ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar dados
          </Button>
          <Button variant="ghost" disabled={savingData} onClick={cancelEdit}>
            Cancelar
          </Button>
        </div>
      </div>
    );
  };

  const renderDadosViagem = () => {
    const locked = trip?.status === 'fechada' || trip?.status === 'cancelada';
    return (
      <div className="space-y-6">
        {renderViagemMeta()}
        <Separator />
        {isEditing ? renderOperationalEdit() : renderOperationalView()}
        {locked && (
          <p className="text-xs text-muted-foreground">
            Viagem fechada é imutável. Correções são registradas por errata.
          </p>
        )}
      </div>
    );
  };

  // --- Section 5: Anexos e ocorrências (Phase 9 — REQ-29/REQ-11) ---

  const attachmentIcon = (att) => {
    if (att.kind === 'note' || !att.has_file) return <MessageSquare className="w-4 h-4 text-muted-foreground shrink-0" />;
    if (att.kind === 'image' || (att.content_type || '').startsWith('image/')) return <ImageIcon className="w-4 h-4 text-primary shrink-0" />;
    return <FileText className="w-4 h-4 text-primary shrink-0" />;
  };

  const fmtBytes = (n) => {
    if (!n || n <= 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderAnexos = () => (
    <div className="space-y-5">
      {/* Formulário de upload — só quando editável */}
      {editable ? (
        <div className="space-y-3 rounded-lg border p-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <Input
              ref={fileInputRef}
              type="file"
              className="text-sm"
              disabled={uploading}
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
          </div>
          <Textarea
            placeholder="Observação / ocorrência (opcional) — pode anexar só texto, sem arquivo."
            value={uploadNote}
            disabled={uploading}
            onChange={(e) => setUploadNote(e.target.value)}
            rows={2}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Imagens, PDF e documentos até 25 MB.
            </p>
            <Button
              size="sm"
              className="gap-2"
              onClick={handleUpload}
              disabled={uploading || (!uploadFile && !uploadNote.trim())}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Anexar
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Viagem fechada/cancelada é imutável — anexos não podem ser adicionados ou removidos.
        </p>
      )}

      {/* Lista de anexos */}
      {attachmentsLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando anexos…
        </div>
      ) : attachments.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum anexo ou observação registrada.</p>
      ) : (
        <ul className="space-y-2">
          {attachments.map((att) => (
            <li key={att.id} className="flex items-start gap-3 rounded-md border p-2.5">
              {attachmentIcon(att)}
              <div className="flex-1 min-w-0">
                {att.has_file ? (
                  <p className="text-sm font-medium truncate">{att.filename}</p>
                ) : (
                  <p className="text-sm font-medium text-muted-foreground">Observação</p>
                )}
                {att.note && (
                  <p className="text-sm whitespace-pre-wrap mt-0.5">{att.note}</p>
                )}
                <p className="text-xs text-muted-foreground mt-0.5">
                  {att.uploaded_by?.name || att.uploaded_by?.username || '—'}
                  {att.uploaded_at ? ` · ${formatDateTime(att.uploaded_at)}` : ''}
                  {att.has_file && att.size ? ` · ${fmtBytes(att.size)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {att.has_file && (
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Baixar" onClick={() => handleDownload(att)}>
                    <Download className="w-4 h-4" />
                  </Button>
                )}
                {editable && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    title="Excluir"
                    onClick={() => handleDeleteAttachment(att)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // --- Section 6: Briefing climático (Phase 9 — REQ-30) ---

  const renderBriefingClimatico = () => {
    if (weatherLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando briefing climático…
        </div>
      );
    }
    const briefing = weather?.briefing;
    const archived = weather?.archived;
    if (!briefing) {
      return <p className="text-sm text-muted-foreground">Briefing climático indisponível.</p>;
    }
    const unavailable = briefing.status === 'unavailable';
    const alert = !!briefing.alert;
    return (
      <div className="space-y-4">
        {/* Banner de alerta / estado */}
        <div
          className={`rounded-lg border p-3 flex items-start gap-3 ${
            unavailable
              ? 'border-muted bg-muted/30'
              : alert
              ? 'border-amber-500/50 bg-amber-50 dark:bg-amber-950/20'
              : 'border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/20'
          }`}
        >
          {unavailable ? (
            <CloudSun className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          ) : alert ? (
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          ) : (
            <CloudSun className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium">{briefing.alert_text || '—'}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {archived ? 'Snapshot arquivado no fechamento (imutável).' : 'Previsão ao vivo (preventiva).'}
              {briefing.source ? ` · Fonte: ${briefing.source}` : ''}
            </p>
          </div>
        </div>

        {/* Resumo por local (origem/destino) */}
        {!unavailable && Array.isArray(briefing.locations) && briefing.locations.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {briefing.locations.map((loc, i) => {
              const s = loc.summary || {};
              return (
                <div key={i} className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold">{loc.name}</p>
                    {loc.bad_weather && (
                      <Badge variant="outline" className="text-amber-700 border-amber-400 gap-1">
                        <AlertTriangle className="w-3 h-3" /> Tempo ruim
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Wind className="w-3 h-3" /> Rajada máx
                    </span>
                    <span className="font-medium text-right">
                      {s.max_wind_gust_kmh != null ? `${Math.round(s.max_wind_gust_kmh)} km/h` : '—'}
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Droplets className="w-3 h-3" /> Chuva máx
                    </span>
                    <span className="font-medium text-right">
                      {s.max_precipitation_mm != null ? `${s.max_precipitation_mm} mm` : '—'}
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Droplets className="w-3 h-3" /> Prob. chuva
                    </span>
                    <span className="font-medium text-right">
                      {s.max_precipitation_probability != null ? `${Math.round(s.max_precipitation_probability)}%` : '—'}
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Wind className="w-3 h-3" /> Vento (direção)
                    </span>
                    <span className="font-medium text-right">
                      {s.wind_cardinal
                        ? `${s.wind_cardinal} (${s.wind_from_deg}°)${s.mean_wind_speed_kmh != null ? ` · ${Math.round(s.mean_wind_speed_kmh)} km/h` : ''}`
                        : '—'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Vento na navegação — análise por perna NO HORÁRIO previsto (REQ-30 vetor de vento) */}
        {!unavailable && briefing.navigation_wind && (
          <div className="rounded-md border p-3 space-y-2">
            <p className="text-sm font-semibold flex items-center gap-2">
              <Wind className="w-4 h-4" /> Vento na navegação
            </p>
            <p className="text-xs text-muted-foreground">
              Análise por perna no horário previsto (ida e volta avaliadas separadamente).
              Vento a favor (de popa) ajuda a navegação; contra (de proa) atrapalha.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[briefing.navigation_wind.loaded, briefing.navigation_wind.empty]
                .filter(Boolean)
                .map((leg, i) => {
                  const tone =
                    leg.bad_weather || leg.verdict === 'contra'
                      ? 'red'
                      : leg.verdict === 'a_favor'
                      ? 'green'
                      : 'amber';
                  const toneCls = {
                    green:
                      'border-emerald-500/60 bg-emerald-50/40 dark:bg-emerald-950/20 shadow-[0_0_12px_-2px_rgba(16,185,129,0.55)]',
                    red:
                      'border-red-500/60 bg-red-50/40 dark:bg-red-950/20 shadow-[0_0_12px_-2px_rgba(239,68,68,0.55)]',
                    amber:
                      'border-amber-500/60 bg-amber-50/40 dark:bg-amber-950/20 shadow-[0_0_12px_-2px_rgba(245,158,11,0.55)]',
                  }[tone];
                  const badgeCls = {
                    green: 'text-emerald-700 border-emerald-400',
                    red: 'text-red-700 border-red-400',
                    amber: 'text-amber-700 border-amber-400',
                  }[tone];
                  return (
                    <div key={i} className={`rounded-lg border px-3 py-2.5 text-xs space-y-1.5 ${toneCls}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">
                          {leg.leg === 'carregado' ? 'Carregado (ida)' : 'Vazio (volta)'}
                        </span>
                        <Badge variant="outline" className={`shrink-0 ${badgeCls}`}>
                          {leg.verdict === 'a_favor'
                            ? 'Vento a favor'
                            : leg.verdict === 'contra'
                            ? 'Vento contra'
                            : leg.verdict === 'indef'
                            ? 'Vento —'
                            : 'Vento de través'}
                          {leg.component_kmh != null ? ` · ${Math.abs(leg.component_kmh).toFixed(0)} km/h` : ''}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground truncate">
                        {leg.from_name} → {leg.to_name}
                      </p>
                      {leg.window && (
                        <p className="text-muted-foreground">
                          {formatDateTime(leg.window.start)} → {formatDateTime(leg.window.end)}
                        </p>
                      )}
                      <p className="text-muted-foreground">
                        Vento {leg.wind_cardinal || '—'}
                        {leg.wind_from_deg != null ? ` (${leg.wind_from_deg}°)` : ''}
                        {leg.max_wind_gust_kmh != null ? ` · rajada ${Math.round(leg.max_wind_gust_kmh)} km/h` : ''}
                        {leg.bad_weather ? ' · tempo ruim' : ''}
                      </p>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- Lançamento manual (equipamento sem telemetria): horímetro, combustível, início+fim ---
  const renderManualActual = () => {
    const locked = trip?.status === 'fechada' || trip?.status === 'cancelada';
    return (
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Equipamento sem telemetria: lance manualmente os dados realizados da viagem. O ciclo é o
          mesmo do monitorado — abrir despacho, acompanhar (volume/tripulação) e confirmar fechamento.
        </p>

        {locked ? (
          <p className="text-sm text-muted-foreground">
            Viagem {trip.status === 'fechada' ? 'fechada' : 'cancelada'} — lançamento manual
            indisponível. Correções são registradas por errata.
          </p>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
              <div className="space-y-1.5">
                <Label htmlFor="man_horimetro">Horímetro (horas)</Label>
                <Input
                  id="man_horimetro"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.1"
                  value={manHorimetro}
                  disabled={savingManual}
                  placeholder="ex.: 12.5"
                  onChange={(e) => setManHorimetro(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="man_fuel">Combustível total (L)</Label>
                <Input
                  id="man_fuel"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="1"
                  value={manFuel}
                  disabled={savingManual}
                  placeholder="ex.: 1800"
                  onChange={(e) => setManFuel(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Início da viagem</Label>
                <DateTimePicker24h
                  value={manStartedAt}
                  onChange={setManStartedAt}
                  ariaLabel="Início da viagem"
                  disabled={savingManual}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Fim da viagem</Label>
                <DateTimePicker24h
                  value={manEndedAt}
                  onChange={setManEndedAt}
                  ariaLabel="Fim da viagem"
                  disabled={savingManual}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button className="gap-2" disabled={savingManual} onClick={handleManualActual}>
                {savingManual ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Lançar realizado
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- Zona de ação do header: controles do ciclo de despacho (UI-SPEC §A) ---
  // Dirigido por trip.status + trip.dispatch?.opened_at. NUNCA muta status no cliente.

  const renderCycleControls = () => {
    const status = trip?.status;
    const dispatchOpened = !!trip?.dispatch?.opened_at;

    // Estado Imutável: substitui os botões do ciclo por um chip estático.
    if (status === 'fechada' || status === 'cancelada') {
      return (
        <Badge
          variant="outline"
          className="gap-2 text-muted-foreground border-muted py-1.5 px-3"
        >
          <Lock className="w-3.5 h-3.5" />
          {status === 'fechada' ? 'Viagem fechada — imutável' : 'Viagem cancelada'}
        </Badge>
      );
    }

    const canOpen = (status === 'planejada' || status === 'em_andamento') && !dispatchOpened;
    const showLive = status === 'em_andamento' || status === 'fechamento_proposto';
    const canClose = status === 'fechamento_proposto';

    return (
      <div className="flex items-center gap-3 flex-wrap">
        {canOpen && (
          <Button className="gap-2" onClick={handleOpenDispatch} disabled={opening}>
            {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Abrir despacho
          </Button>
        )}

        {showLive && (
          <Badge variant="outline" className="gap-2 py-1.5 px-3 text-primary border-primary/40">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-pulse" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <Radio className="w-3.5 h-3.5" />
            Ao vivo
          </Badge>
        )}

        {canClose ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="gap-2" disabled={closing}>
                {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Fechar despacho
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Fechar despacho?</AlertDialogTitle>
                <AlertDialogDescription>
                  Após o fechamento, a viagem fica <strong>imutável</strong> (NFR-01). Correções
                  futuras exigem uma errata. Confirme que os dados da viagem estão corretos.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirmClosure}>Fechar despacho</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span wrapper: botão disabled não dispara eventos de Tooltip por si só */}
                <span tabIndex={0}>
                  <Button variant="outline" className="gap-2" disabled>
                    <Lock className="w-4 h-4" />
                    Fechar despacho
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                O fechamento fica disponível quando o sistema propõe o fechamento da viagem.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  };

  // --- Render principal ---

  const statusInfo = trip ? STATUS_BADGE[trip.status] || { label: trip.status, cls: 'bg-muted text-muted-foreground border-transparent' } : null;

  return (
    <div className="page-container space-y-6" data-testid="trip-detail-page">
      {/* Back nav — SEMPRE visível */}
      <Button
        variant="ghost"
        size="sm"
        className="gap-2 -ml-2 mb-2"
        onClick={() => navigate('/operations/trips')}
      >
        <ChevronLeft className="w-4 h-4" />
        Viagens
      </Button>

      {/* Ramos de render */}
      {loading && renderSkeleton()}

      {!loading && error === 'not_found' && renderNotFound()}

      {!loading && error === 'server_error' && renderServerError()}

      {!loading && !error && trip && (
        <>
          {/* Cabeçalho da página */}
          <div className="pl-12 md:pl-0">
            <div className="flex items-center gap-3 flex-wrap justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <Route className="w-6 h-6 text-primary shrink-0" />
                <h1 className="text-2xl font-semibold font-mono">{trip.id}</h1>
                {statusInfo && (
                  <Badge className={`rounded-md text-xs font-semibold ${statusInfo.cls}`}>
                    {statusInfo.label}
                  </Badge>
                )}
                {trip.no_telemetry && (
                  <Badge variant="outline" className="rounded-md text-xs">
                    Sem telemetria
                  </Badge>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 shrink-0"
                onClick={() => navigate(`/operations/trips/${tripId}/print`)}
              >
                <FileText className="w-4 h-4" />
                Imprimir / PDF
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {trip.equipment_name || ''}
              {trip.equipment_tag ? ` (${trip.equipment_tag})` : ''}
              {(trip.equipment_name || trip.equipment_tag) ? ' · ' : ''}
              Criada em {trip.created_at ? formatDateTime(trip.created_at) : '—'}
            </p>
          </div>

          {/* Zona de ação — controles do ciclo de despacho (UI-SPEC §A) */}
          <div className="pl-12 md:pl-0">{renderCycleControls()}</div>

          {/* Section — Índice de desempenho (PROTAGONISTA — área nobre, logo abaixo do header).
              renderIndices() já retorna a própria Card; só aparece quando há realizado/alvo. */}
          {renderIndices()}

          {/* Section: Lançamento manual — somente equipamento sem telemetria (D-15) */}
          {trip.no_telemetry && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-3">
                  <Pencil className="w-4 h-4 text-primary" />
                  Lançamento manual (equipamento sem telemetria)
                </CardTitle>
              </CardHeader>
              <CardContent>{renderManualActual()}</CardContent>
            </Card>
          )}

          {/* Section 1.5 — Totais da viagem (_totais server-side) */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-3">
                <Sigma className="w-4 h-4 text-primary" />
                Totais da viagem
              </CardTitle>
            </CardHeader>
            <CardContent>{renderTotais()}</CardContent>
          </Card>

          {/* Section — Detalhes: previsto × realizado (SECUNDÁRIO — rebaixado do topo;
              contexto de planejamento, densidade menor, sem competir com o índice). */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground flex items-center gap-3">
                <BarChart2 className="w-4 h-4" />
                Detalhes: previsto × realizado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderPrevisto()}
                {renderRealizado()}
              </div>
            </CardContent>
          </Card>

          {/* Section 2 — Etapas */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-3">
                <Layers className="w-4 h-4 text-primary" />
                Etapas
              </CardTitle>
            </CardHeader>
            <CardContent>{renderEtapas()}</CardContent>
          </Card>

          {/* Section 3 — Consumo por motor */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-3">
                <Fuel className="w-4 h-4 text-primary" />
                Consumo por motor
              </CardTitle>
            </CardHeader>
            <CardContent>{renderConsumoMotor()}</CardContent>
          </Card>

          {/* Section 4 — Dados da viagem (leitura ↔ edição in-place) */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base flex items-center gap-3">
                  <ClipboardList className="w-4 h-4 text-primary" />
                  Dados da viagem
                </CardTitle>
                {/* Editar: só na janela editável (planejada/em_andamento/fechamento_proposto) e fora do modo edição */}
                {!isEditing &&
                  ['planejada', 'em_andamento', 'fechamento_proposto'].includes(trip.status) && (
                    <Button variant="ghost" size="sm" className="gap-2" onClick={enterEdit}>
                      <Pencil className="w-4 h-4" />
                      Editar
                    </Button>
                  )}
              </div>
            </CardHeader>
            <CardContent>{renderDadosViagem()}</CardContent>
          </Card>

          {/* Section 5 — Anexos e ocorrências (Phase 9 — REQ-29/REQ-11) */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-3">
                <Paperclip className="w-4 h-4 text-primary" />
                Anexos e ocorrências
              </CardTitle>
            </CardHeader>
            <CardContent>{renderAnexos()}</CardContent>
          </Card>

          {/* Section 6 — Briefing climático (Phase 9 — REQ-30) */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-3">
                <CloudSun className="w-4 h-4 text-primary" />
                Briefing climático
              </CardTitle>
            </CardHeader>
            <CardContent>{renderBriefingClimatico()}</CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default TripDetailPage;
